import axios from 'axios'
import { LogtoClientError, LogtoError, LogtoRequestError, OidcError } from '@logto/browser'

let authEpoch = 0
export const currentAuthEpoch = () => authEpoch
export const beginAuthTransition = () => ++authEpoch

/** sub из id-токена. Нужен, чтобы отличить обновление того же входа от смены человека. */
export function sessionSubject(idToken: string | null): string | null {
  if (!idToken) return null
  const payload = idToken.split('.')[1]
  if (!payload) return null
  try {
    const padded = payload.replace(/-/g, '+').replace(/_/g, '/')
    const base64 = padded.padEnd(padded.length + (4 - padded.length % 4) % 4, '=')
    const json = JSON.parse(atob(base64)) as { sub?: unknown }
    return typeof json.sub === 'string' ? json.sub : null
  } catch {
    return null
  }
}

export function describeAuthFailure(error: unknown): string {
  if (error instanceof LogtoRequestError)
    return `${error.code}: ${error.message}`
  if (error instanceof OidcError)
    return [error.error, error.errorDescription].filter(Boolean).join(': ')
  if (error instanceof LogtoClientError || error instanceof LogtoError)
    return `${error.code}: ${error.message}`
  if (axios.isAxiosError(error)) {
    const status = error.response?.status
    const method = error.config?.method?.toUpperCase()
    const url = urlWithoutQuery(error.config?.url)
    return [status, method, url, error.message].filter(value => value !== undefined && value !== '').join(' ')
  }
  if (error instanceof Error)
    return error.message
  return 'неизвестная ошибка авторизации'
}

function urlWithoutQuery(url?: string): string | undefined {
  if (!url) return undefined
  try {
    const parsed = new URL(url)
    return `${parsed.origin}${parsed.pathname}`
  } catch {
    return url.split('?')[0]
  }
}

export function isInvalidGrant(error: unknown): boolean {
  if (error instanceof OidcError)
    return error.error === 'invalid_grant'
  /* Refresh-токен Logto отклоняет как LogtoRequestError, не как OidcError. */
  if (error instanceof LogtoRequestError)
    return error.code === 'oidc.invalid_grant' || error.code === 'invalid_grant'
  return false
}

export function isExpiredAuth(error: unknown): boolean {
  return (error instanceof LogtoClientError && error.code === 'not_authenticated')
    || isInvalidGrant(error)
    || (axios.isAxiosError(error) && error.response?.status === 401)
}

export function isTemporaryAuthFailure(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false
  const status = error.response?.status
  return error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED'
    || status === 408 || status === 429 || (status !== undefined && status >= 500)
}

/** Repeat only a read of IAM; never replay code exchanges or refresh requests. */
export async function readAuthProfile<T>(
  read: () => Promise<T>,
  refreshAccessToken?: () => Promise<void>,
): Promise<T> {
  let retried = false
  let refreshed = false
  for (;;) {
    try {
      return await read()
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 401
        && refreshAccessToken && !refreshed) {
        refreshed = true
        await refreshAccessToken()
        continue
      }
      if (isTemporaryAuthFailure(error) && !retried) {
        retried = true
        await new Promise(resolve => setTimeout(resolve, 500))
        continue
      }
      throw error
    }
  }
}
